import { h, FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AcceptPage from '~src/domains/accept/AcceptPage';
import AdversePage from '~src/domains/adverse/AdversePage';
import DocumentView from '~src/domains/common/components/DocumentView';
import Wrapper from '~src/domains/common/components/Wrapper';
import { OrderLoadStatus, Page } from '~src/domains/common/enums/api';
import {
  setOfferId,
  setOrderLoadStatus,
} from '~src/domains/common/state/commonReducer';
import * as commonSelectors from '~src/domains/common/state/commonSelectors';
import ErrorPage from '~src/domains/error/ErrorPage';
import FaqPage from '~src/domains/faq/FaqPage';
import FullSSNPage from '~src/domains/fullSSN/FullSSNPage';
import InitialPage from '~src/domains/initial/InitialPage';
import InvalidAddressPage from '~src/domains/invalidAddress/InvalidAddressPage';
import LoanConsentPage from '~src/domains/loanConsent/LoanConsentPage';
import PayPage from '~src/domains/pay/PayPage';
import ReacceptPage from '~src/domains/reaccept/ReacceptPage';
import ReapplyPage from '~src/domains/reapply/ReapplyPage';
import StartPage from '~src/domains/start/StartPage';
import TripExpiredPage from '~src/domains/tripExpired/TripExpiredPage';
import VerifyPage from '~src/domains/verify/VerifyPage';
import NOIPage from '~src/domains/noi/NOIPage';
import OutOfFilterPage from '~src/domains/outOfFilter/OutOfFilterPage';
import CreditFreezePage from '~src/domains/creditFreeze/CreditFreezePage';
import { MAX_POLL_CALLS } from '~src/utils/constants';
import { useInterval } from '~src/utils/hooks';
import { trackPageMount, trackXhrPollTimeout } from '~src/utils/tracking';
import { xhrOrders, xhrPoll } from '~src/utils/xhr';

const EmptyPage: FunctionComponent = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(trackPageMount(Page.EMPTY));
  });

  return <div></div>;
};

const App: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const isOfferExpired = useSelector(commonSelectors.isOfferExpiredSelector);
  const orderId = useSelector(commonSelectors.orderIdSelector);
  const page = useSelector(commonSelectors.pageSelector);
  const pollCallCount = useSelector(commonSelectors.pollCallCountSelector);
  const currDoc = useSelector(commonSelectors.documentSelector);
  const mcode = useSelector(commonSelectors.mcodeSelector);

  const getPageContent = () => {
    if (currDoc.show) return <DocumentView />;

    switch (page) {
      case Page.INITIAL:
        return <InitialPage />;
      case Page.START:
        return <StartPage />;
      case Page.VERIFY:
        return <VerifyPage />;
      case Page.ACCEPT:
        return <AcceptPage />;
      case Page.FULL_SSN:
        return <FullSSNPage />;
      case Page.LOAN_CONSENT:
        return <LoanConsentPage />;
      case Page.PAY:
        return <PayPage />;
      case Page.REACCEPT:
      case Page.RECONFIRM:
        return <ReacceptPage />;
      case Page.REAPPLY:
        if (mcode === 14) {
          return <TripExpiredPage />;
        }
        return <ReapplyPage />;
      case Page.ADVERSE:
        return <AdversePage />;
      case Page.ERROR:
        return <ErrorPage />;
      case Page.INVALID_ADDRESS:
        return <InvalidAddressPage />;
      case Page.TRIP_EXPIRED:
        return <TripExpiredPage />;
      case Page.FAQ:
        return <FaqPage />;
      case Page.NOI:
        return <NOIPage />;
      case Page.OUT_OF_FILTER:
        return <OutOfFilterPage />;
      case Page.CREDIT_FREEZE:
        return <CreditFreezePage />;
      default:
        return <EmptyPage />;
    }
  };

  /** Set offer id and offer info in state. */
  useEffect(() => {
    const { hash } = window.location;
    const [, urlOfferId] = hash.split('/');

    if (!urlOfferId) {
      dispatch(setOrderLoadStatus(OrderLoadStatus.FAILED));
      return;
    }

    dispatch(setOfferId(urlOfferId));
    dispatch(xhrOrders(urlOfferId, i18n));
  }, []);

  useEffect(() => {
    if (pollCallCount >= MAX_POLL_CALLS) {
      dispatch(trackXhrPollTimeout());
    }
  }, [pollCallCount]);

  useInterval(async () => {
    // Don't poll if offer id doesn't exist, the offer is expired, or the poll count is greater or equal to the max
    if (!orderId?.length || pollCallCount >= MAX_POLL_CALLS || isOfferExpired) {
      return;
    }

    dispatch(xhrPoll());
  }, 5000);

  return <Wrapper>{getPageContent()}</Wrapper>;
};

export default App;
